import React, { useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Instagram, Youtube, ArrowUpRight } from 'lucide-react';
import { Reveal } from './Reveal';

// TikTok glyph
const TikTokIcon = ({ size = 18, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const EASE = [0.16, 1, 0.3, 1];

const HeroSection = () => {
  const { t, language } = useLanguage();
  const sectionRef = useRef(null);
  const portraitRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Parallax for portrait + headline
  const portraitY = useTransform(scrollYProgress, [0, 1], ['0%', '18%']);
  const portraitScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
  const headlineY = useTransform(scrollYProgress, [0, 1], ['0%', '-12%']);
  const headlineOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const socialLinks = [
    { name: 'TikTok', Icon: TikTokIcon, url: 'https://www.tiktok.com/@renihamikus', count: '126K' },
    { name: 'Instagram', Icon: Instagram, url: 'http://instagram.com/renatahamikus', count: '85K' },
    { name: 'YouTube', Icon: Youtube, url: 'https://youtube.com/@renatahamikus', count: '50K' },
  ];

  const tickerWords = language === 'hu'
    ? ['Wellness', 'Motiváció', 'Szépség', 'Inspiráció', 'Életstílus', 'Szabadság']
    : ['Wellness', 'Motivation', 'Beauty', 'Inspiration', 'Lifestyle', 'Freedom'];

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen w-full overflow-hidden bg-background"
      data-testid="hero-section"
    >
      {/* Ambient gradient + soft circles */}
      <div className="pointer-events-none absolute inset-0 -z-0">
        <div className="absolute -top-40 -right-40 w-[680px] h-[680px] rounded-full bg-gradient-to-br from-[#E8D6B0]/40 via-[#F0ECE3]/30 to-transparent blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full bg-gradient-to-tr from-[#E1D9CD]/40 to-transparent blur-3xl" />
      </div>

      <div className="relative max-w-[1480px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center min-h-[80vh]">
          {/* LEFT: Editorial text block */}
          <motion.div
            style={{ y: headlineY, opacity: headlineOpacity }}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            {/* Overline */}
            <Reveal as="div" duration={0.9} y={20} className="mb-8 flex items-center gap-4">
              <span className="block w-12 h-px bg-accent" />
              <span className="font-body text-[0.72rem] uppercase tracking-[0.32em] text-text-secondary">
                {language === 'hu' ? 'Wellness · Motiváció · Szépség' : 'Wellness · Motivation · Beauty'}
              </span>
            </Reveal>

            {/* Massive serif name */}
            <Reveal as="h1" duration={1.2} y={32} delay={0.05} className="font-heading text-[14vw] leading-[0.88] tracking-[-0.02em] text-text-primary md:text-[8.5vw] lg:text-[7vw] xl:text-[6.5rem]">
              <span className="block font-light">Renata</span>
              <span className="block italic font-extralight text-text-primary/90 -mt-1 md:-mt-2">Hamikus<span className="text-accent">.</span></span>
            </Reveal>

            {/* Tagline */}
            <Reveal as="p" delay={0.25} duration={1} className="mt-10 font-heading text-2xl md:text-3xl italic text-text-secondary max-w-xl leading-snug">
              {language === 'hu'
                ? 'Inspiráció a mindennapokra — kívül és belül.'
                : 'Daily inspiration — inside and out.'}
            </Reveal>

            {/* CTAs */}
            <Reveal as="div" delay={0.4} className="mt-12 flex flex-wrap items-center gap-5 md:gap-7">
              <a
                href="#course"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('course')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-magnetic group inline-flex items-center gap-4 bg-text-primary text-background pl-7 pr-3 py-3 rounded-full transition-all duration-700 hover:bg-accent"
                data-testid="hero-cta-course"
              >
                <span className="font-body text-[0.78rem] uppercase tracking-[0.25em]">
                  {t.course.cta}
                </span>
                <span className="flex items-center justify-center w-10 h-10 rounded-full bg-background text-text-primary group-hover:rotate-45 transition-transform duration-700">
                  <ArrowUpRight size={16} strokeWidth={1.5} />
                </span>
              </a>

              <a
                href="#work-with-me"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('work-with-me')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="link-underline font-body text-[0.78rem] uppercase tracking-[0.25em] text-text-primary"
                data-testid="hero-cta-collaborate"
              >
                {t.nav.workWithMe}
              </a>
            </Reveal>

            {/* Social row */}
            <Reveal as="div" delay={0.55} className="mt-16 flex items-center flex-wrap gap-x-8 gap-y-4">
              <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-text-secondary">
                {t.hero.followMe}
              </span>
              <div className="flex items-center gap-7">
                {socialLinks.map((s) => (
                  <a
                    key={s.name}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center gap-2.5 text-text-primary"
                    data-testid={`hero-social-${s.name.toLowerCase()}`}
                  >
                    <s.Icon size={16} className="opacity-70 group-hover:opacity-100 group-hover:text-accent transition-all" />
                    <span className="font-body text-[0.78rem] tracking-[0.15em] text-text-primary/80 group-hover:text-accent transition-colors">
                      {s.count}
                    </span>
                  </a>
                ))}
              </div>
            </Reveal>
          </motion.div>

          {/* RIGHT: Portrait card with silk treatment */}
          <div className="lg:col-span-5 order-1 lg:order-2 relative">
            <motion.div
              ref={portraitRef}
              initial={{ opacity: 0, y: 40, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 1.4, ease: EASE, delay: 0.3 }}
              className="relative mx-auto w-full max-w-[480px]"
            >
              {/* Silk backdrop layers */}
              <div className="absolute -inset-6 rounded-[28px] bg-gradient-to-br from-[#D8C49A] via-[#E8D6B0] to-[#C5A059] opacity-70 blur-[1px]" />
              <div className="absolute -inset-10 rounded-[36px] bg-gradient-to-tr from-[#E8E2D7] to-transparent" />
              {/* Gold hairline frame */}
              <div className="absolute -inset-3 rounded-[20px] border border-accent/40" />

              <motion.div
                style={{ y: portraitY, scale: portraitScale }}
                className="relative aspect-[3/4] rounded-[16px] overflow-hidden shadow-editorial grain-layer"
              >
                <img
                  src="/img/renata-hero.webp"
                  alt="Renata Hamikus"
                  className="w-full h-full object-cover"
                  data-testid="hero-image"
                />
                {/* Soft warm wash */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#2C2825]/30 via-transparent to-transparent" />
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent to-[#C5A059]/10" />
              </motion.div>

              {/* Floating editorial caption */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.2, delay: 1, ease: EASE }}
                className="absolute -bottom-6 left-6 right-6 md:-bottom-8 md:-left-8 md:right-auto md:max-w-[260px] bg-background/90 backdrop-blur-md border border-border/70 rounded-2xl px-6 py-5 shadow-soft"
              >
                <p className="font-body text-[0.62rem] uppercase tracking-[0.3em] text-accent mb-2">
                  {language === 'hu' ? 'Az Új Fejezet' : 'A New Chapter'}
                </p>
                <p className="font-heading italic text-lg leading-snug text-text-primary">
                  {language === 'hu'
                    ? '„Légy az a nő, akire mindig vágytál."'
                    : '"Become the woman you\'ve always dreamed of."'}
                </p>
              </motion.div>

              {/* Floating tag — followers */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1.2, delay: 1.2, ease: EASE }}
                className="hidden md:flex absolute -top-4 -right-6 items-center gap-3 bg-text-primary text-background px-5 py-3 rounded-full"
              >
                <span className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                <span className="font-body text-[0.7rem] uppercase tracking-[0.25em]">
                  260K+ {language === 'hu' ? 'követő' : 'community'}
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Marquee ticker */}
      <div className="relative border-t border-border/60 overflow-hidden py-5 md:py-6">
        <div className="marquee-track gap-12 whitespace-nowrap font-heading italic text-2xl md:text-3xl text-text-primary/70">
          {[...tickerWords, ...tickerWords, ...tickerWords].map((w, i) => (
            <span key={i} className="flex items-center gap-12 pr-12">
              {w}
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-accent" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
